import { isNativeTokenAddress } from "./nativeToken";

/** Percentage of native token balance to reserve for gas when clicking MAX. */
export const NATIVE_TOKEN_GAS_BUFFER_PERCENT = 5n;

/** Conservative default cap for EVM chains not in the explicit map (0.01 ETH). */
const DEFAULT_EVM_CAP = 10_000_000_000_000_000n;

/**
 * Chain-aware absolute cap so the buffer matches real gas costs.
 * Buffer = min(5% of balance, cap for the chain).
 *
 * Examples (ETH on Ethereum mainnet, chainId 1, cap 0.05 ETH):
 *   0.01 ETH  → buffer 0.0005 (5%)   → MAX = 0.0095 ETH
 *   1 ETH     → buffer 0.05 (cap)     → MAX = 0.95 ETH
 *   10 ETH    → buffer 0.05 (cap)     → MAX = 9.95 ETH
 *   100 ETH   → buffer 0.05 (cap)     → MAX = 99.95 ETH
 *
 * Examples (ETH on Base, chainId 8453, cap 0.001 ETH):
 *   0.01 ETH  → buffer 0.0005 (5%)   → MAX = 0.0095 ETH
 *   1 ETH     → buffer 0.001 (cap)    → MAX = 0.999 ETH
 *   10 ETH    → buffer 0.001 (cap)    → MAX = 9.999 ETH
 *
 * Examples (MATIC on Polygon, chainId 137, cap 0.1 MATIC):
 *   1 MATIC   → buffer 0.05 (5%)     → MAX = 0.95 MATIC
 *   100 MATIC → buffer 0.1 (cap)      → MAX = 99.9 MATIC
 *
 * ERC-20 tokens (USDC, WETH, etc.) pass through unchanged — gas is paid
 * in the native coin, so full balance is safe.
 *
 * Unknown EVM chains fall back to DEFAULT_EVM_CAP (0.01 ETH).
 * Solana uses its own entry (0.01 SOL).
 */
const ABSOLUTE_CAP_BY_CHAIN: Record<number, bigint> = {
  // Ethereum mainnet — higher cap to cover gas spikes
  1: 50_000_000_000_000_000n, // 0.05 ETH
  // ETH L2s — cheap gas
  10: 1_000_000_000_000_000n, // 0.001 ETH — Optimism
  130: 1_000_000_000_000_000n, // 0.001 ETH — Unichain
  324: 1_000_000_000_000_000n, // 0.001 ETH — zkSync Era
  480: 1_000_000_000_000_000n, // 0.001 ETH — World
  1868: 1_000_000_000_000_000n, // 0.001 ETH — Soneium
  2741: 1_000_000_000_000_000n, // 0.001 ETH — Abstract
  8333: 1_000_000_000_000_000n, // 0.001 ETH — B3
  8453: 1_000_000_000_000_000n, // 0.001 ETH — Base
  34443: 1_000_000_000_000_000n, // 0.001 ETH — Mode
  42161: 1_000_000_000_000_000n, // 0.001 ETH — Arbitrum
  59144: 1_000_000_000_000_000n, // 0.001 ETH — Linea
  81457: 1_000_000_000_000_000n, // 0.001 ETH — Blast
  534352: 1_000_000_000_000_000n, // 0.001 ETH — Scroll
  // Non-ETH native coin chains
  56: 5_000_000_000_000_000n, // 0.005 BNB — BSC
  137: 100_000_000_000_000_000n, // 0.1 MATIC — Polygon
  1101: 1_000_000_000_000_000n, // 0.001 ETH — Polygon zkEVM
  5000: 1_000_000_000_000_000n, // 0.001 MNT — Mantle
  43114: 50_000_000_000_000_000n, // 0.05 AVAX — Avalanche
  80094: 50_000_000_000_000_000n, // 0.05 BERA — Berachain
  // Solana
  7565164: 10_000_000n, // 0.01 SOL
};

/** Subtract a gas buffer for native tokens on MAX; non-native tokens pass through unchanged. */
export function applyNativeGasBuffer(amountWei: bigint, tokenAddress: string | undefined, chainId: number): bigint {
  if (!tokenAddress || !isNativeTokenAddress(tokenAddress)) return amountWei;
  const percentBuffer = (amountWei * NATIVE_TOKEN_GAS_BUFFER_PERCENT) / 100n;
  const absoluteCap = ABSOLUTE_CAP_BY_CHAIN[chainId] ?? DEFAULT_EVM_CAP;
  const buffer = percentBuffer < absoluteCap ? percentBuffer : absoluteCap;
  const result = amountWei - buffer;
  return result > 0n ? result : 0n;
}
